import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, ElementRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TypKod, ZpetnaVazbaConfig } from './zpetna-vazba.model';
import { RecaptchaService } from '../services/recaptcha';
import { LocalizePipe } from '../pipes/LocalizePipe';

const defaultConfig: ZpetnaVazbaConfig = {
  recaptchaSiteKey: '',
  recaptchaRenderUrl: '',
  aktualniUzivatelEmail: '',
}

@Component({
  selector: 'app-zpetna-vazba',
  imports: [CommonModule, LocalizePipe, ReactiveFormsModule],
  templateUrl: './zpetna-vazba.html',
  styleUrl: './zpetna-vazba.css',
})
export class ZpetnaVazba implements OnInit {
  private apiUrl = '/zpetna-vazba/api/odeslani';
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private recaptchaService = inject(RecaptchaService);

  // Signals pro stav UI
  isOpen = signal(false);
  isSubmitting = signal(false);
  isSuccess = signal(false);
  isEmailFocused = signal(false);
  isTextFocused = signal(false);
  errorMessage = signal('');
  private types$ = this.http.get<TypKod[]>('/zpetna-vazba/api/typy');
  feedbackTypes = toSignal(this.types$, { initialValue: [] });
  config = input(defaultConfig, {
    alias: 'config',
    transform: (value: string | object): ZpetnaVazbaConfig => {
      let parsedData = {};
      if (typeof value === 'string') {
        try {
          parsedData = JSON.parse(value);
        } catch (e) {
          console.error('Chyba parsování configu:', e);
        }
      } else if (typeof value === 'object' && value !== null) {
        parsedData = value;
      }
      return { ...defaultConfig, ...parsedData };
    }
  })

  zpetnaVazbaForm = this.fb.group({
    text: ['', [Validators.required, Validators.maxLength(300)]],
    typKod: [null, Validators.required],
    email: ['', [Validators.email]],
  });

  // Obsah modálu — viewChild signál se aktualizuje při vzniku/zániku @if bloku
  modalContent = viewChild<ElementRef<HTMLElement>>('modalContent');

  hasValueTypKod = toSignal(
    this.zpetnaVazbaForm.controls.typKod.valueChanges, 
    { initialValue: this.zpetnaVazbaForm.controls.typKod.value }
  );

  hasValueText = toSignal(
    this.zpetnaVazbaForm.controls.text.valueChanges, 
    { initialValue: this.zpetnaVazbaForm.controls.text.value }
  );

   hasValueEmail = toSignal(
    this.zpetnaVazbaForm.controls.email.valueChanges, 
    { initialValue: this.zpetnaVazbaForm.controls.email.value }
  );

  constructor() {
    effect(() => {
      this.fillUserEmail();
    });

    // přesunutí a zviditelnění grecaptcha-badge po zobrazení modálu
    // vrácení zpět řeší toggleForm()
    effect(() => {
      const content = this.modalContent();
      if (content) {
        this.recaptchaService.showBadgeIn(content.nativeElement);
      }
    });
  }

  isTextActive = computed(() => {
    const hasFocus = this.isTextFocused();
    const hasValue = !!this.hasValueText();
    return hasFocus || hasValue;
  });
  isEmailActive = computed(() => {
    const hasFocus = this.isEmailFocused();
    const hasValue = !!this.hasValueEmail();
    return hasFocus || hasValue;
  });

  ngOnInit() {
    const siteKey = this.config().recaptchaSiteKey;
    const renderUrl = this.config().recaptchaRenderUrl;
    if (siteKey) {
      this.recaptchaService.loadScript(siteKey, renderUrl).then(() => {
        this.recaptchaService.hideBadge();
      });
    }
  }

  toggleForm() {
    // přesunutí badge do rootu před odstraním modálu z DOM
    if (this.isOpen()) {
      this.recaptchaService.restoreBadge();
    }
    this.isOpen.update(v => !v);
    this.isSuccess.set(false);
    this.fillUserEmail();
  }

  private fillUserEmail() {
    const userEmail = this.config().aktualniUzivatelEmail;
    if (userEmail && !this.zpetnaVazbaForm.controls.email.dirty){
      this.zpetnaVazbaForm.controls.email.setValue(userEmail);
    }
  }

  async onSubmit(event: Event) {
    event.stopPropagation();
    if (this.zpetnaVazbaForm.invalid) return;

    this.isSubmitting.set(true);
    const siteKey = this.config().recaptchaSiteKey;
    const renderUrl = this.config().recaptchaRenderUrl;
    this.errorMessage.set('');

    let recaptchaToken = '';
    if (siteKey) {
      try {
        recaptchaToken = await this.recaptchaService.execute(siteKey, renderUrl, 'submit_zpetnaVazba');
      } catch (error) {
        console.error('Recaptcha error:', error);
        this.isSubmitting.set(false);
        this.errorMessage.set(window.custom?.l10n?.localize('zv.form.recaptchaChybovaHlaska'))
        return;
      }
    }

    const payload = {
      ...this.zpetnaVazbaForm.value,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      recaptchaToken: recaptchaToken,
    };

    this.http.post(this.apiUrl, payload).subscribe({
      next: () => {
        this.isSuccess.set(true);
        this.zpetnaVazbaForm.reset();
        this.isSubmitting.set(false);
      },
      error: (err) => {
        console.error(err);
        this.errorMessage.set(err.error.message);
        this.isSubmitting.set(false);
      }
    });
  }
}
